/* Personalize popup
 * -------------------------------------------------------------
 * - Opens from PDP "Personalize" button.
 * - Live engraving preview over bottle image.
 * - Adds perfume + Personalisation charge together.
 * - Uses Shopify Markets/Catalog pricing automatically.
 * - Supports editing an existing personalised cart item.
 * - Links perfume and Personalisation charge using a unique ID.
 * -------------------------------------------------------------
 */

class PersonalizePopup extends HTMLElement {
  constructor() {
    super();

    this.dialog = this.querySelector('.personalize-popup__dialog');
    this.form = this.querySelector('[data-personalize-form]');

    this.inputs = {
      1: this.querySelector('[data-engraving-input="1"]'),
      2: this.querySelector('[data-engraving-input="2"]'),
    };

    this.previews = {
      1: this.querySelector('[data-engraving-preview="1"]'),
      2: this.querySelector('[data-engraving-preview="2"]'),
    };

    this.submitBtn = this.querySelector('[data-personalize-submit]');
    this.errorEl = this.querySelector('[data-personalize-error]');

    this._bind();
    this._syncPreview();
  }

  /* ---------------------------------------------------------
   * Events
   * --------------------------------------------------------- */
  _bind() {
    this.querySelectorAll('[data-personalize-close]').forEach((el) => {
      el.addEventListener('click', () => this.close());
    });

    Object.values(this.inputs).forEach((input) => {
      if (!input) return;

      input.addEventListener('input', () => {
        this._syncPreview();
      });
    });

    if (this.submitBtn) {
      this.submitBtn.addEventListener('click', (e) => {
        this._onSubmit(e);
      });
    }

    this.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        this.close();
      }
    });
  }

  /* ---------------------------------------------------------
   * Live preview
   * --------------------------------------------------------- */
  _syncPreview() {
    const v1 = (this.inputs[1]?.value || '').trim();
    const v2 = (this.inputs[2]?.value || '').trim();

    if (this.previews[1]) {
      this.previews[1].textContent = v1;
    }

    if (this.previews[2]) {
      this.previews[2].textContent = v2;
    }

    const mode = this.dataset.mode || 'add';
    const hasText = v1.length + v2.length > 0;

    if (this.submitBtn) {
      this.submitBtn.disabled = mode === 'add' ? !hasText : false;
    }
  }

  /* ---------------------------------------------------------
   * Open popup
   * --------------------------------------------------------- */
  open() {
    this._syncPreview();

    if (this.errorEl) {
      this.errorEl.hidden = true;
      this.errorEl.textContent = '';
    }

    this.removeAttribute('hidden');
    document.body.classList.add('overflow-hidden');

    setTimeout(() => {
      if (this.inputs[1]) {
        this.inputs[1].focus();
      }
    }, 50);
  }

  /* ---------------------------------------------------------
   * Close popup
   * --------------------------------------------------------- */
  close() {
    this.setAttribute('hidden', '');
    document.body.classList.remove('overflow-hidden');
    this.classList.remove('is-loading');
  }

  /* ---------------------------------------------------------
   * Submit Personalisation
   * --------------------------------------------------------- */
  async _onSubmit(e) {
    e.preventDefault();

    const v1 = (this.inputs[1]?.value || '').trim();
    const v2 = (this.inputs[2]?.value || '').trim();

    const mode = this.dataset.mode || 'add';

    if (mode === 'add' && !v1 && !v2) {
      return;
    }

    const perfumeVariantId = this.dataset.variantId;
    const personalisationVariantId =
      this.dataset.personalisationVariantId;

    if (!perfumeVariantId) {
      this._showError('Perfume variant is not available.');
      return;
    }

    if (!personalisationVariantId) {
      this._showError(
        'Personalisation product is not configured.'
      );
      return;
    }

    const qty =
      parseInt(this.dataset.lineQty || '1', 10) || 1;

    const addQty = mode === 'edit' ? qty : 1;

    this.classList.add('is-loading');

    if (this.submitBtn) {
      this.submitBtn.disabled = true;
    }

    if (this.errorEl) {
      this.errorEl.hidden = true;
      this.errorEl.textContent = '';
    }

    try {
      let personalisationId;

      /* -----------------------------------------------------
       * EDIT MODE
       *
       * Find old personalised perfume + linked charge,
       * remove them, then recreate with updated engraving.
       * ----------------------------------------------------- */
      if (mode === 'edit' && this.dataset.lineKey) {
        const existingData =
          await this._getPersonalisationData(
            this.dataset.lineKey
          );

        if (existingData) {
          personalisationId =
            existingData.personalisationId;

          await this._removeLinkedItems(
            this.dataset.lineKey,
            personalisationId
          );
        }
      }

      /* -----------------------------------------------------
       * Create unique link between perfume and charge.
       * ----------------------------------------------------- */
      if (!personalisationId) {
        personalisationId =
          this._generatePersonalisationId();
      }

      /* -----------------------------------------------------
       * Perfume line item properties
       * ----------------------------------------------------- */
      const perfumeProperties = {};

      if (v1) {
        perfumeProperties['Engraving Line 1'] = v1;
      }

      if (v2) {
        perfumeProperties['Engraving Line 2'] = v2;
      }

      /*
       * Property starting with "_" is a private Shopify
       * line-item property. It links the perfume to the
       * Personalisation charge.
       */
      perfumeProperties['_Personalisation ID'] =
        personalisationId;

      perfumeProperties['_Personalised'] = 'Yes';

      /* -----------------------------------------------------
       * Personalisation charge properties
       * ----------------------------------------------------- */
      const personalisationProperties = {
        '_Personalisation ID': personalisationId,
        '_Personalisation For Product':
          this.dataset.productId || '',
      };

      /* -----------------------------------------------------
       * Add BOTH products to Shopify cart
       *
       * 1. Actual perfume
       * 2. Hidden Personalisation product
       * ----------------------------------------------------- */
      const cartUrl =
        `${this.dataset.cartUrl || '/cart'}/add.js`;

      const res = await fetch(cartUrl, {
        method: 'POST',

        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },

        body: JSON.stringify({
          items: [
            {
              id: Number(perfumeVariantId),
              quantity: addQty,
              properties: perfumeProperties,
            },

            {
              id: Number(personalisationVariantId),
              quantity: addQty,
              properties: personalisationProperties,
            },
          ],
        }),
      });

      if (!res.ok) {
        const err = await res
          .json()
          .catch(() => ({}));

        throw new Error(
          err.description ||
            err.message ||
            'Unable to add personalised product to cart.'
        );
      }

      /* -----------------------------------------------------
       * Refresh cart drawer
       * ----------------------------------------------------- */
      await this._refreshCartDrawer();

      this.close();

      this._openCartDrawer();
    } catch (err) {
      console.error(
        'Personalisation error:',
        err
      );

      this._showError(
        err.message ||
          'Something went wrong. Please try again.'
      );
    } finally {
      this.classList.remove('is-loading');

      if (this.submitBtn) {
        this.submitBtn.disabled = false;
      }

      this._syncPreview();
    }
  }

  /* ---------------------------------------------------------
   * Generate unique Personalisation ID
   *
   * Example:
   * personalise-17575599999-xh2a8c
   * --------------------------------------------------------- */
  _generatePersonalisationId() {
    return (
      'personalise-' +
      Date.now() +
      '-' +
      Math.random()
        .toString(36)
        .substring(2, 9)
    );
  }

  /* ---------------------------------------------------------
   * Get existing personalisation data while editing.
   * --------------------------------------------------------- */
  async _getPersonalisationData(lineKey) {
    try {
      const res = await fetch('/cart.js', {
        headers: {
          Accept: 'application/json',
        },
      });

      if (!res.ok) {
        return null;
      }

      const cart = await res.json();

      const item = cart.items.find(
        (cartItem) =>
          cartItem.key === lineKey
      );

      if (!item) {
        return null;
      }

      const personalisationId =
        item.properties?.[
          '_Personalisation ID'
        ];

      return {
        item,
        personalisationId,
      };
    } catch (error) {
      console.error(
        'Unable to get cart item:',
        error
      );

      return null;
    }
  }

  /* ---------------------------------------------------------
   * Remove perfume + matching Personalisation charge.
   *
   * Required when editing so a second charge does not get
   * created.
   * --------------------------------------------------------- */
  async _removeLinkedItems(
    perfumeLineKey,
    personalisationId
  ) {
    const cartRes = await fetch('/cart.js', {
      headers: {
        Accept: 'application/json',
      },
    });

    if (!cartRes.ok) {
      throw new Error(
        'Could not read the current cart.'
      );
    }

    const cart = await cartRes.json();

    /*
     * Find linked Personalisation charge.
     */
    const linkedCharge = cart.items.find(
      (item) => {
        if (item.key === perfumeLineKey) {
          return false;
        }

        return (
          personalisationId &&
          item.properties?.[
            '_Personalisation ID'
          ] === personalisationId
        );
      }
    );

    /*
     * Remove Personalisation charge first.
     */
    if (linkedCharge) {
      await this._removeLine(
        linkedCharge.key
      );
    }

    /*
     * Then remove perfume.
     */
    await this._removeLine(
      perfumeLineKey
    );
  }

  /* ---------------------------------------------------------
   * Remove specific cart line
   * --------------------------------------------------------- */
  async _removeLine(lineKey) {
    const res = await fetch(
      '/cart/change.js',
      {
        method: 'POST',

        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },

        body: JSON.stringify({
          id: lineKey,
          quantity: 0,
        }),
      }
    );

    if (!res.ok) {
      const error = await res
        .json()
        .catch(() => ({}));

      throw new Error(
        error.description ||
          'Could not update the cart item.'
      );
    }

    return res.json();
  }

  /* ---------------------------------------------------------
   * Refresh cart drawer
   * --------------------------------------------------------- */
  async _refreshCartDrawer() {
    try {
      const cartUrl =
        this.dataset.cartUrl || '/cart';

      /*
       * Refresh drawer
       */
      const res = await fetch(
        `${cartUrl}?section_id=cart-drawer`
      );

      const html = await res.text();

      const parser =
        new DOMParser().parseFromString(
          html,
          'text/html'
        );

      const newDrawer =
        parser.querySelector('cart-drawer');

      const oldDrawer =
        document.querySelector('cart-drawer');

      if (newDrawer && oldDrawer) {
        oldDrawer.replaceWith(
          newDrawer
        );
      }

      /*
       * Refresh cart bubble
       */
      const bubbleRes = await fetch(
        '/?section_id=cart-icon-bubble'
      );

      const bubbleHtml =
        await bubbleRes.text();

      const bubbleDoc =
        new DOMParser().parseFromString(
          bubbleHtml,
          'text/html'
        );

      const newBubble =
        bubbleDoc.querySelector(
          '#cart-icon-bubble'
        );

      const oldBubble =
        document.querySelector(
          '#cart-icon-bubble'
        );

      if (newBubble && oldBubble) {
        oldBubble.innerHTML =
          newBubble.innerHTML;
      }
    } catch (error) {
      console.warn(
        'Cart drawer refresh failed:',
        error
      );
    }
  }

  /* ---------------------------------------------------------
   * Open cart drawer
   * --------------------------------------------------------- */
  _openCartDrawer() {
    const drawer =
      document.querySelector(
        'cart-drawer'
      );

    if (
      drawer &&
      typeof drawer.open === 'function'
    ) {
      drawer.open();
    }
  }

  /* ---------------------------------------------------------
   * Error helper
   * --------------------------------------------------------- */
  _showError(message) {
    if (!this.errorEl) return;

    this.errorEl.textContent =
      message;

    this.errorEl.hidden = false;
  }
}

/* -----------------------------------------------------------
 * Register component
 * ----------------------------------------------------------- */
if (
  !customElements.get(
    'personalize-popup'
  )
) {
  customElements.define(
    'personalize-popup',
    PersonalizePopup
  );
}

/* -----------------------------------------------------------
 * Open from PDP Personalize button
 * ----------------------------------------------------------- */
document.addEventListener(
  'click',
  (e) => {
    const btn = e.target.closest(
      '[data-personalize-open]'
    );

    if (!btn) return;

    e.preventDefault();

    const targetId =
      btn.dataset.popupId ||
      btn.getAttribute(
        'aria-controls'
      );

    const popup = targetId
      ? document.getElementById(
          targetId
        )
      : document.querySelector(
          'personalize-popup'
        );

    if (
      popup &&
      typeof popup.open ===
        'function'
    ) {
      popup.open();
    }
  }
);

/* -----------------------------------------------------------
 * Open from cart drawer Engraving/Edit button
 * ----------------------------------------------------------- */
document.addEventListener(
  'click',
  (e) => {
    const link = e.target.closest(
      '[data-engraving-edit]'
    );

    if (!link) return;

    e.preventDefault();

    const targetId =
      link.dataset.popupId ||
      link.getAttribute(
        'aria-controls'
      );

    if (!targetId) return;

    const popup =
      document.getElementById(
        targetId
      );

    if (
      popup &&
      typeof popup.open ===
        'function'
    ) {
      popup.open();
    }
  }
);